import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { Dispatch } from 'redux';
import { ImportItemStatus } from '@synergy/models';
import { PaginatedResponse } from '@synergy/api';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';

import { Api } from '../../../../../api';
import { AppState } from '../../../../../store/models/app-state';
import { ImportItem } from '../../models/import-item';
import { ImportJob } from '../../models/import-job';
import { ImportJobDetail } from '../../models/import-job-detail';

export const COMPUTE_ITEM_CHANGES_STARTED = 'COMPUTE_ITEM_CHANGES_STARTED';
export const DELETE_IMPORT = 'DELETE_IMPORT';
export const PUBLISH_ARTICLES_COMPLETED = 'PUBLISH_ARTICLES_COMPLETED';
export const IMPORT_ABORTED = 'IMPORT_ABORTED';
export const PUBLISH_ALL_COMPLETED = 'PUBLISH_ALL_COMPLETED';
export const RECEIVE_IMPORT_DETAILS = 'RECEIVE_IMPORT_DETAILS';
export const RECEIVE_IMPORT_JOB_ITEMS = 'RECEIVE_IMPORT_JOB_ITEMS';
export const RECEIVE_IMPORTS = 'RECEIVE_IMPORTS';
export const REMOVE_ARTICLES_COMPLETED = 'REMOVE_ARTICLES_COMPLETED';
export const RETRY_FAILED_COMPLETED = 'RETRY_FAILED_COMPLETED';
export const REQUEST_COMPUTE_ITEM_CHANGES = 'REQUEST_COMPUTE_ITEM_CHANGES';
export const REQUEST_RETRY_FAILED = 'REQUEST_RETRY_FAILED';
export const REQUEST_IMPORT_DETAILS = 'REQUEST_IMPORT_DETAILS';
export const REQUEST_IMPORT_JOB_ITEMS = 'REQUEST_IMPORT_JOB_ITEMS';
export const REQUEST_IMPORTS = 'REQUEST_IMPORTS';
export const REQUEST_IMPORTS_END = 'REQUEST_IMPORTS_END';
export const RESET_IMPORTS = 'RESET_IMPORTS';

export const IMPORT_CHANGE_SETTINGS = 'IMPORT_CHANGE_SETTINGS';
export const IMPORT_CHANGE_SETTINGS_PUBLISH_DATE = 'IMPORT_CHANGE_SETTINGS_PUBLISH_DATE';

export const requestImports = () => {
    return {
        type: REQUEST_IMPORTS,
    };
};

const requestImportDetails = () => {
    return {
        type: REQUEST_IMPORT_DETAILS,
    };
};

const requestImportJobItems = () => {
    return {
        type: REQUEST_IMPORT_JOB_ITEMS,
    };
};

const requestComputeItemChanges = () => {
    return {
        type: REQUEST_COMPUTE_ITEM_CHANGES,
    };
};

const requestRetryFailed = () => {
    return {
        type: REQUEST_RETRY_FAILED,
    };
};

const requestImportsEnd = () => {
    return {
        type: REQUEST_IMPORTS_END,
    };
};

export const resetImports = () => {
    return {
        type: RESET_IMPORTS,
    };
};

const receiveImports = (imports: PaginatedResponse<ImportJob>) => {
    return {
        type: RECEIVE_IMPORTS,
        imports,
    };
};

const receiveImportDetails = (importDetails: ImportJobDetail) => {
    return {
        type: RECEIVE_IMPORT_DETAILS,
        importDetails,
    };
};

const importDeleted = (importId: number) => {
    return {
        type: DELETE_IMPORT,
        importId,
    };
};

const receiveImportJobItems = (importItems: PaginatedResponse<ImportItem>) => {
    return {
        type: RECEIVE_IMPORT_JOB_ITEMS,
        importItems,
    };
};

const removeArticlesCompleted = (articleIds: number[]) => {
    return {
        type: REMOVE_ARTICLES_COMPLETED,
        articleIds,
    };
};

const importAborted = (importId: number) => {
    return {
        type: IMPORT_ABORTED,
        importId,
    };
};

const publishArticlesCompleted = (articleIds: number[]) => {
    return {
        type: PUBLISH_ARTICLES_COMPLETED,
        articleIds,
    };
};

const computeItemChangesStarted = () => {
    return {
        type: COMPUTE_ITEM_CHANGES_STARTED,
    };
};

const retryFailedCompleted = () => {
    return {
        type: RETRY_FAILED_COMPLETED,
    };
};

const publishAllCompleted = () => {
    return {
        type: PUBLISH_ALL_COMPLETED,
    };
};

export const fetchImports = (searchParams: URLSearchParams) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(requestImports());

        const response = await api.import.getImports(searchParams);
        if (response.success) {
            dispatch(receiveImports(response.result));
        } else {
            dispatch(requestImportsEnd());
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const deleteImport = (importId: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        const response = await api.import.delete(importId);
        if (response.success) {
            dispatch(importDeleted(importId));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const publishArticles = (importId: number, articleIds: number[]) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const response = await api.import.publishArticles(importId, articleIds);
        if (response.success) {
            dispatch(publishArticlesCompleted(articleIds));

            await fetchImportDetails(importId)(dispatch, getState, { api });
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const removeArticle = (importId: number, articleIds: number[]) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const response = await api.import.removeArticles(importId, articleIds);
        if (response.success) {
            dispatch(removeArticlesCompleted(articleIds));

            await fetchImportDetails(importId)(dispatch, getState, { api });
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const abortImport = (importId: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        const response = await api.import.abort(importId);
        if (response.success) {
            dispatch(importAborted(importId));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const publishAllImports = (importId: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        const response = await api.import.publishAll(importId);
        if (response.success) {
            dispatch(publishAllCompleted());
            dispatch(Snackbar.enqueue(PoolNotification.success('app.imports.publishAll.success')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const fetchImportDetails = (importId: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(requestImportDetails());

        const response = await api.import.getImportDetails(importId);
        if (response.success) {
            dispatch(receiveImportDetails(response.result));
        } else {
            dispatch(requestImportsEnd());
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const fetchImportJobItems = (importId: number, searchParams: URLSearchParams, statuses: ImportItemStatus[]) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch(requestImportJobItems());

        const searchParamsWithStatus = new URLSearchParams(searchParams);
        searchParamsWithStatus.set('status', statuses.join(','));
        const response = await api.import.getImportItems(importId, searchParamsWithStatus);
        if (response.success) {
            dispatch(receiveImportJobItems(response.result));

            await fetchImportDetails(importId)(dispatch, getState, { api });
        } else {
            dispatch(requestImportsEnd());
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const computeItemChanges = (importId: number) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch(requestComputeItemChanges());

        const response = await api.import.computeItemChanges(importId);
        if (response.success) {
            dispatch(computeItemChangesStarted());

            await fetchImportDetails(importId)(dispatch, getState, { api });
        } else {
            dispatch(computeItemChangesStarted());
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const retryFailed = (importId: number) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch(requestRetryFailed());

        const response = await api.import.retryFailed(importId);
        if (response.success) {
            dispatch(retryFailedCompleted());

            await fetchImportDetails(importId)(dispatch, getState, { api });
        } else {
            dispatch(retryFailedCompleted());
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const retryWarning = (importId: number) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch(requestRetryFailed());

        const response = await api.import.retryWarning(importId);
        if (response.success) {
            dispatch(retryFailedCompleted());

            await fetchImportDetails(importId)(dispatch, getState, { api });
        } else {
            dispatch(retryFailedCompleted());
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const toggleShouldDeleteExisting = (importId: number, shouldDeleteExisting: boolean) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: IMPORT_CHANGE_SETTINGS, shouldDeleteExisting });
        const response = await api.import.postImportDetailSettings({ importId, shouldDeleteExisting });
        if (!response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const toggleShouldSkipCategoryCreation = (importId: number, shouldSkipCategoryCreation: boolean) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: IMPORT_CHANGE_SETTINGS, shouldSkipCategoryCreation });
        const response = await api.import.postImportDetailSettings({ importId, shouldSkipCategoryCreation });
        if (!response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const toggleKeepSpecialPrices = (importId: number, keepSpecialPrices: boolean) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: IMPORT_CHANGE_SETTINGS, keepSpecialPrices });
        const response = await api.import.postImportDetailSettings({ importId, keepSpecialPrices });
        if (!response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const toggleShouldOverwriteFiles = (importId: number, shouldOverwriteFiles: boolean) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: IMPORT_CHANGE_SETTINGS, shouldOverwriteFiles });
        const response = await api.import.postImportDetailSettings({ importId, shouldOverwriteFiles });
        if (!response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const toggleShouldNotifySupplier = (importId: number, shouldNotifySupplier: boolean) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: IMPORT_CHANGE_SETTINGS, shouldNotifySupplier });
        const response = await api.import.postImportDetailSettings({ importId, shouldNotifySupplier });
        if (!response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const setPublishDate = (importId: number, publishDate: string | null) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: IMPORT_CHANGE_SETTINGS_PUBLISH_DATE, publishDate });
        const response = await api.import.postImportDetailSettings({ importId, publishDate });
        if (!response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};
